import { AlertTriangle, TrendingUp, ArrowRight, Minus } from 'lucide-react';

/* Couleurs EXACTES par statut (spécification BENCHORY — VERROUILLÉ).
   NE PAS MODIFIER ces couleurs — validées par le fondateur. */
const STATUS_CONFIG = {
  excellent:      { label: 'Excellent',            badgeBg: '#EAF3DE', badgeText: '#27500A', bar: '#639922', ring: '#639922', icon: TrendingUp },
  above:          { label: 'Au-dessus du marché',  badgeBg: '#EAF3DE', badgeText: '#27500A', bar: '#3B6D11', ring: '#3B6D11', icon: TrendingUp },
  average:        { label: 'Dans la moyenne',      badgeBg: '#F0ECD3', badgeText: '#5A5A12', bar: '#8B8B2A', ring: '#8B8B2A', icon: ArrowRight },
  warning:        { label: 'Mérite attention',     badgeBg: '#FCEBEB', badgeText: '#791F1F', bar: '#A32D2D', ring: '#A32D2D', icon: AlertTriangle },
  non_calculable: { label: 'Données manquantes',   badgeBg: '#f5f5f5', badgeText: '#999',    bar: '#ddd',    ring: '#ddd',    icon: Minus },
};

/* ─── Couleurs des 4 segments de barre (VERROUILLÉ) ─── */
const segColor = (status, idx) => {
  const map = {
    excellent: ['#B5D89C', '#93C572', '#73AF4B', '#639922'],
    above:     ['#A8CF8A', '#83B85E', '#5E9B2F', '#3B6D11'],
    average:   ['#E0DEB5', '#C8C67A', '#A8A53E', '#8B8B2A'],
    warning:   ['#F5C4C4', '#E69A9A', '#D06A6A', '#A32D2D'],
  };
  return (map[status] || map.average)[idx] || '#ddd';
};

/* ─── Donut Ring SVG ─── */
function DonutRing({ pct, color, size = 88, stroke = 7, children }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const fill = circ * Math.min(Math.max(pct / 100, 0), 1);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f0f0" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${fill} ${circ - fill}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.7s ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

/* ─── Barre de position avec 4 segments discrets + gap 3px ─── */
function PositionBar({ value, min, q1, median, q3, max, status, unit, formatType }) {
  const range = max - min || 1;
  const pos = ((value - min) / range) * 100;
  const clampedPos = Math.min(Math.max(pos, 1.5), 98.5);
  const color = (STATUS_CONFIG[status] || STATUS_CONFIG.average).bar;

  const seg = [
    Math.max(((q1 - min) / range) * 100, 2),
    Math.max(((median - q1) / range) * 100, 2),
    Math.max(((q3 - median) / range) * 100, 2),
    Math.max(((max - q3) / range) * 100, 2),
  ];
  const total = seg.reduce((a, b) => a + b, 0);
  const norm = seg.map(s => (s / total) * 100);

  const fmt = (v) => {
    if (formatType === 'percentage' || unit === '%') return `${v.toFixed(1)} %`;
    if (unit === 'jours') return `${v.toFixed(1)} j`;
    if (unit === '\u20ac' || unit === '\u20ac/ETP') return `${Math.round(v).toLocaleString('fr-FR')} \u20ac`;
    return Number.isInteger(v) ? v.toString() : v.toFixed(1);
  };

  const markers = [
    { label: 'Bas', val: min, pct: 0 },
    { label: 'Q1', val: q1, pct: ((q1 - min) / range) * 100 },
    { label: 'M\u00e9d.', val: median, pct: ((median - min) / range) * 100 },
    { label: 'Q3', val: q3, pct: ((q3 - min) / range) * 100 },
    { label: 'Haut', val: max, pct: 100 },
  ];

  return (
    <div className="mt-3 mb-1.5">
      <p className="text-[10px] text-gray-400 font-semibold tracking-wide uppercase mb-1.5">Votre position actuelle</p>
      <div className="relative" style={{ height: 10 }}>
        {/* 4 segments with 3px gap */}
        <div className="flex items-center h-full" style={{ gap: 3 }}>
          {norm.map((w, i) => (
            <div key={i} style={{
              width: `${w}%`, height: '100%',
              background: segColor(status, i),
              borderRadius: i === 0 ? '5px 0 0 5px' : i === 3 ? '0 5px 5px 0' : 0,
            }} />
          ))}
        </div>
        {/* Position dot on top */}
        <div className="absolute top-1/2" style={{
          left: `${clampedPos}%`, transform: 'translate(-50%, -50%)',
          width: 14, height: 14, borderRadius: '50%',
          background: 'white', border: `2.5px solid ${color}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }} />
      </div>
      {/* Marker labels */}
      <div className="relative h-7 mt-1">
        {markers.map((m, i) => (
          <div key={i} className="absolute text-center" style={{ left: `${m.pct}%`, transform: 'translateX(-50%)' }}>
            <span className="text-[8px] text-gray-400 font-medium block leading-none">{m.label}</span>
            <span className="text-[9px] text-gray-600 font-bold leading-tight">{fmt(m.val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Barre d'effort envisageable ─── */
function EffortBar({ value, cible, min, max, higher, status }) {
  const range = max - min || 1;
  const valPct = ((value - min) / range) * 100;
  const ciblePct = ((cible - min) / range) * 100;
  const startPct = Math.min(valPct, ciblePct);
  const endPct = Math.max(valPct, ciblePct);
  const clampStart = Math.max(startPct, 1);
  const clampEnd = Math.min(endPct, 99);
  const barColor = (STATUS_CONFIG[status] || STATUS_CONFIG.average).bar;

  return (
    <div className="mb-3 rounded-lg px-3 py-2" style={{ background: '#f8f9fa' }}>
      <p className="text-[10px] text-gray-500 font-medium mb-1.5">Effort envisageable — atteindre la médiane *</p>
      <div className="relative" style={{ height: 4, background: '#e5e7eb', borderRadius: 2 }}>
        <div className="absolute inset-y-0 rounded-full" style={{
          left: `${clampStart}%`, width: `${Math.max(clampEnd - clampStart, 2)}%`,
          background: `linear-gradient(90deg, ${barColor}88, ${barColor})`,
        }} />
        <div className="absolute top-1/2" style={{
          left: `${Math.min(Math.max(ciblePct, 2), 98)}%`,
          transform: 'translate(-50%, -50%)',
          width: 8, height: 8, borderRadius: '50%',
          background: 'white', border: `2px solid ${barColor}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        }} />
      </div>
    </div>
  );
}


export function KpiCard({ kpi }) {
  const cfg = STATUS_CONFIG[kpi.status] || STATUS_CONFIG.non_calculable;
  const StatusIcon = cfg.icon;

  const impactPositive = kpi.gap_euros >= 0;
  const impactAbs = Math.abs(kpi.gap_euros || 0);

  const trendPositive = kpi.trend !== null && kpi.trend !== undefined
    ? (kpi.higher_is_better ? kpi.trend >= 0 : kpi.trend <= 0)
    : null;

  if (!kpi.calculable) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 opacity-50" data-testid={`kpi-card-${kpi.key}`}>
        <h3 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3">{kpi.label}</h3>
        <p className="text-xs text-gray-400">Donnée non renseignée ce mois-ci — à compléter dans votre espace saisie.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col" data-testid={`kpi-card-${kpi.key}`}>
      {/* Title */}
      <h3 className="text-[11px] font-bold text-gray-600 uppercase tracking-wider mb-3 leading-tight">{kpi.label}</h3>

      {/* Top row: Donut + Status + Comparisons */}
      <div className="flex items-start gap-4 mb-1">
        <DonutRing pct={kpi.percentile} color={cfg.ring}>
          <span className="text-[12px] font-bold text-gray-700 text-center leading-none whitespace-nowrap" style={{ fontSize: (kpi.formatted_value || '').length > 8 ? 10 : 12 }}>{kpi.formatted_value}</span>
        </DonutRing>
        <div className="flex-1 min-w-0 pt-1">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border" style={{
            background: cfg.badgeBg, color: cfg.badgeText, borderColor: cfg.badgeBg,
          }}>
            <StatusIcon size={10} />
            {cfg.label}
          </span>
          <p className="text-xs text-gray-500 mt-1.5">
            vs médiane{' '}
            <strong className="text-gray-600">
              {kpi.format_type === 'percentage' || kpi.unit === '%' ? `${kpi.cohort_median.toFixed(1)} %` :
               kpi.unit === 'jours' ? `${kpi.cohort_median.toFixed(1)} j` :
               kpi.unit === '\u20ac' || kpi.unit === '\u20ac/ETP' ? `${Math.round(kpi.cohort_median).toLocaleString('fr-FR')} \u20ac` :
               kpi.cohort_median.toFixed(1)}
            </strong>
          </p>
          {kpi.trend_label && (
            <p className="text-[10px] mt-0.5 font-medium" style={{ color: trendPositive ? '#3B6D11' : '#A32D2D' }}>
              {trendPositive ? '\u2191' : '\u2193'} {kpi.trend_label}
            </p>
          )}
        </div>
      </div>

      {/* Position bar with 4 discrete segments + 3px gap */}
      <PositionBar value={kpi.value} min={kpi.cohort_min} q1={kpi.cohort_q1} median={kpi.cohort_median}
        q3={kpi.cohort_q3} max={kpi.cohort_max} status={kpi.status} unit={kpi.unit} formatType={kpi.format_type} />

      {/* Definition — texte dirigeant sous la barre de position */}
      {kpi.definition && (
        <p className="text-[11px] text-gray-500 leading-relaxed mb-3" data-testid={`kpi-def-${kpi.key}`}>{kpi.definition}</p>
      )}

      {/* Effort bar */}
      {kpi.is_below_median && kpi.cible_effort !== null && kpi.cible_effort !== undefined && (
        <EffortBar value={kpi.value} cible={kpi.cible_effort} min={kpi.cohort_min} max={kpi.cohort_max} higher={kpi.higher_is_better} status={kpi.status} />
      )}

      {/* Separator */}
      <div className="border-t border-gray-100 mt-auto" />

      {/* Financial impact */}
      {impactAbs > 0 && (
        <div className="pt-2.5">
          <p className="text-[10px] text-gray-400 font-medium italic">Ce que cette position vous rapporte</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: impactPositive ? '#27500A' : '#791F1F' }}>
            {impactPositive ? '+' : '\u2212'} {impactAbs.toLocaleString('fr-FR')} € / mois
          </p>
          {kpi.is_below_median && kpi.gain_effort > 0 && (
            <p className="text-[10px] font-medium mt-0.5" style={{ color: '#3B6D11' }}>
              * En atteignant la médiane : + {kpi.gain_effort.toLocaleString('fr-FR')} € récupérables
            </p>
          )}
        </div>
      )}
      {impactAbs === 0 && kpi.euro_detail && (
        <div className="pt-2.5">
          <p className="text-[10px] text-gray-400 italic">{kpi.euro_detail}</p>
        </div>
      )}
    </div>
  );
}

export default KpiCard;
